/**
 * Utility functions for getting user location and IP address
 */

/**
 * Get user's current position using geolocation API
 * @returns {Promise<{latitude: number | null, longitude: number | null}>}
 */
export const getCurrentPosition = () =>
	new Promise((resolve, reject) => {
		if (!navigator.geolocation) {
			reject(new Error('Geolocation is not supported by this browser'));

			return;
		}

		navigator.geolocation.getCurrentPosition(
			(position) => {
				resolve({
					latitude: position.coords.latitude,
					longitude: position.coords.longitude,
				});
			},
			(error) => {
				console.warn('Error getting location:', error);
				// Return null coordinates if geolocation fails
				resolve({
					latitude: null,
					longitude: null,
				});
			},
			{
				enableHighAccuracy: true,
				timeout: 10000,
				maximumAge: 300000, // 5 minutes
			}
		);
	});

/**
 * Get user's IP address using a public API
 * @returns {Promise<string | null>}
 */
export const getUserIP = async () => {
	try {
		const response = await fetch('https://api.ipify.org?format=json');
		const data = await response.json();

		return data.ip;
	} catch (error) {
		console.warn('Error getting IP address:', error);

		// Return null if the service fails
		return null;
	}
};

/**
 * Check if geolocation permission is granted, denied, or prompt
 * @returns {Promise<{state: 'granted' | 'denied' | 'prompt' | 'unsupported', canRequest: boolean}>}
 */
export const checkLocationPermission = async () => {
	if (!navigator.geolocation) {
		return { state: 'unsupported', canRequest: false };
	}

	if (!navigator.permissions) {
		// Fallback for browsers that don't support permissions API
		return { state: 'prompt', canRequest: true };
	}

	try {
		const permission = await navigator.permissions.query({ name: 'geolocation' });

		return {
			state: permission.state,
			canRequest: permission.state !== 'denied',
		};
	} catch (error) {
		console.warn('Error checking location permission:', error);

		return { state: 'prompt', canRequest: true };
	}
};

/**
 * Request location permission with user-friendly prompt
 * @returns {Promise<{granted: boolean, position: {latitude: number | null, longitude: number | null}}>}
 */
export const requestLocationPermission = () =>
	new Promise((resolve) => {
		if (!navigator.geolocation) {
			resolve({
				granted: false,
				position: { latitude: null, longitude: null },
			});

			return;
		}

		navigator.geolocation.getCurrentPosition(
			(position) => {
				resolve({
					granted: true,
					position: {
						latitude: position.coords.latitude,
						longitude: position.coords.longitude,
					},
				});
			},
			(error) => {
				console.warn('Location permission denied or error:', error);
				resolve({
					granted: false,
					position: { latitude: null, longitude: null },
				});
			},
			{
				enableHighAccuracy: true,
				timeout: 10000,
				maximumAge: 300000, // 5 minutes
			}
		);
	});

/**
 * Get user location data (IP, latitude, longitude)
 * @returns {Promise<{ip: string | null, latitude: number | null, longitude: number | null}>}
 */
export const getUserLocationData = async () => {
	try {
		const [ip, position] = await Promise.all([getUserIP(), getCurrentPosition()]);

		return {
			ip,
			latitude: position.latitude,
			longitude: position.longitude,
		};
	} catch (error) {
		console.warn('Error getting location data:', error);

		// Return null values if anything fails
		return {
			ip: null,
			latitude: null,
			longitude: null,
		};
	}
};
